import { Link } from "react-router-dom";
import { Seo, ORG_LD, softwareLd, faqLd } from "@/site/seo";
import PosternGate from "@/site/components/PosternGate";
import { HERO, PROBLEMS, COMPARE_TODAY, COMPARE_POSTERN, ROLES, PLANS, CTA, SITE } from "@/site/content";
import { Heading, Reveal } from "@/site/components/Section";
import StageTour from "@/site/components/StageTour";
import Calculator from "@/site/components/Calculator";
import LeadForm from "@/site/components/LeadForm";
import { PlanCards, FaqList } from "@/site/components/Pricing";
import { track } from "@/site/analytics";
import { FAQ } from "@/site/content";

const FLOW = [
  ["Accounts imported", "via CSV, in the wizard"], ["Whitespace found", "surfaced automatically"], ["Priced in CPQ", "within policy"],
  ["Auto-cleared", "no approval needed"], ["Exceptions reviewed", "deal desk or legal only"], ["Customer signs", "public link, no login"],
  ["Closed won", "ARR and CRM update together"], ["Invoice triggered", "finance notified, in their tool"], ["Renewal flagged", "surfaces as new whitespace"],
];

// Splits a headline into words, each animated in with a stagger (see .h1 .word in App.css).
function AnimatedWords({ text, startDelay = 0 }) {
  const words = text.split(" ");
  return words.map((w, i) => (
    <span className="word" style={{ animationDelay: `${startDelay + i * 0.06}s` }} key={i}>
      {w}
      {i < words.length - 1 ? "\u00A0" : ""}
    </span>
  ));
}

const ROLE_ICONS = [
  // Account manager -- person
  <svg width="18" height="18" viewBox="0 0 32 32" fill="none" aria-hidden="true" key="am">
    <circle cx="16" cy="11" r="6" stroke="#fff" strokeWidth="2.5" />
    <path d="M6 28c0-7 4.5-11 10-11s10 4 10 11" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
  </svg>,
  // CSM -- chat bubble (the renewal/check-in conversation)
  <svg width="18" height="18" viewBox="0 0 32 32" fill="none" aria-hidden="true" key="csm">
    <rect x="5" y="7" width="22" height="14" rx="4" stroke="#fff" strokeWidth="2.3" />
    <path d="M12 21l-2 5 7-5" stroke="#fff" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>,
  // Deal desk -- shield with a check (the approval that clears)
  <svg width="18" height="18" viewBox="0 0 32 32" fill="none" aria-hidden="true" key="dd">
    <path d="M16 4l10 3.5v7c0 7-4.5 11.5-10 13.5-5.5-2-10-6.5-10-13.5v-7L16 4z" stroke="#fff" strokeWidth="2.2" strokeLinejoin="round" />
    <path d="M11.5 16l3 3 6-6.5" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>,
  // Legal -- scales of justice
  <svg width="18" height="18" viewBox="0 0 32 32" fill="none" aria-hidden="true" key="legal">
    <path d="M16 5v21M9 26h14M6 9h20" stroke="#fff" strokeWidth="2.3" strokeLinecap="round" />
    <circle cx="6" cy="14" r="3.1" stroke="#fff" strokeWidth="2" />
    <circle cx="26" cy="14" r="3.1" stroke="#fff" strokeWidth="2" />
  </svg>,
  // Leadership -- rising bars (the portfolio view)
  <svg width="18" height="18" viewBox="0 0 32 32" fill="none" aria-hidden="true" key="ld">
    <path d="M7 24V15M14.3 24V10M21.6 24V17M28 24V6" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
  </svg>,
  // Admin -- controls/sliders
  <svg width="18" height="18" viewBox="0 0 32 32" fill="none" aria-hidden="true" key="admin">
    <path d="M6 10h20M6 16h20M6 22h20" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
    <circle cx="12" cy="10" r="2.3" fill="#fff" />
    <circle cx="20" cy="16" r="2.3" fill="#fff" />
    <circle cx="15" cy="22" r="2.3" fill="#fff" />
  </svg>,
];

export default function Home() {
  return (
    <>
      <Seo path="/" jsonLd={[ORG_LD, softwareLd(PLANS), faqLd(FAQ)]} />

      {/* HERO */}
      <section className="hero" data-testid="hero">
        <div className="wrap">
          <div className="hero__grid">
            <div>
              <div className="eyebrow">{HERO.eyebrow}</div>
              <h1 className="h1"><AnimatedWords text={HERO.h1a} /> <span className="accent"><AnimatedWords text={HERO.h1b} startDelay={0.24} /></span></h1>
              <p className="lede">{HERO.audience}</p>
              <div className="hero__ctas">
                <Link to="/pilot" className="btn btn--primary" onClick={() => track("cta_click", { where: "hero" })} data-testid="hero-cta-primary">{CTA.heroPrimary} <span aria-hidden="true">→</span></Link>
                <a href="#calculator" className="btn btn--ghost" onClick={() => track("cta_click", { where: "hero_calc" })}>{CTA.heroSecondary}</a>
              </div>
              <div className="hero__proofline mono" data-testid="hero-pilot-proof">{CTA.pilotProof}</div>
              <div className="hero__audience">{HERO.sub}</div>
            </div>
            <div className="hero__gate"><PosternGate labels={HERO.tags} /></div>
          </div>
        </div>
      </section>

      {/* PROBLEM (merged with the old standalone "Why now" section --
          that was one bare headline with no supporting content, its own
          full section and background swap. Its line now runs as this
          section's lede, right under the title, so the board-level framing
          leads straight into the specific problems below instead of being
          its own empty-feeling scroll stop.) */}
      <section className="section" id="problem">
        <div className="wrap">
          <Heading eyebrow="The problem" title="Expansion is the bigger lever." lede="It's still stuck in spreadsheets and email threads. Net revenue retention is the board's number, but nobody owns the workflow that produces it." />
          {/* Headline-only by default -- the explanation is a click away via
              <details>, so the section stays skimmable but the fuller copy
              isn't lost, just no longer forced onto the first pass. */}
          <div className="cards">
            {PROBLEMS.map((p, i) => (
              <details className="card card--acc" key={p.title}>
                <summary>
                  <div>
                    <div className="card__n">0{i + 1}</div>
                    <div className="card__t">{p.title}</div>
                  </div>
                  <span className="card__chev" aria-hidden="true">+</span>
                </summary>
                <div className="card__b">{p.body}</div>
              </details>
            ))}
          </div>
          <div className="compare" style={{ marginTop: 28 }}>
            <div className="compare__col"><div className="eyebrow" style={{ color: "var(--red)" }}>Today — five tools, one deal</div>
              {COMPARE_TODAY.map((r) => <div className="compare__row" key={r.tool}><span className="dot dot--red" /><span className="compare__tool">{r.tool}</span><span className="compare__desc">{r.text}</span></div>)}</div>
            <div className="compare__vs">vs</div>
            <div className="compare__col compare__col--postern"><div className="eyebrow">Postern — one record, end to end</div>
              {COMPARE_POSTERN.map((t) => <div className="compare__row" key={t}><span className="dot" /><span>{t}</span></div>)}</div>
          </div>
        </div>
      </section>

      {/* PRODUCT TOUR */}
      <section className="section section--paper" id="product">
        <div className="wrap">
          <Heading eyebrow="The product" title="One record, six stages." lede="Most tools stop at finding the opportunity. Postern keeps it as one record — found, priced, approved and signed — with nothing re-typed along the way." />
          <StageTour />
        </div>
      </section>

      {/* GOVERNANCE + CALCULATOR */}
      {/* HOW IT WORKS */}
      <section className="section" id="how-it-works">
        <div className="wrap">
          <Heading eyebrow="How it works" title="Set up once, and everything after runs itself." lede="Bring in the accounts you own, and surface the whitespace still hiding inside them." />
          <div className="cards cards--4">
            {[["Import your accounts", "A wizard helps you set up, from workspace basics to your live accounts."], ["Spot the whitespace", "Every account shows what it hasn't bought, next to what they already pay — renewals flagged before they're a surprise."], ["Price & quote", "Price the deal right on the order form — deals within the discount policy clear automatically, no approval needed."], ["Review & sign", "Approved deals go straight to signature — the customer signs from a link, no account needed, exceptions routed to the right owner first."]].map(([t, b], i) => (
              <Reveal className="card" key={t}>
                <div className="card__top"><div className="card__badge" /><div className="card__step">Step {i + 1}</div></div>
                <div className="card__t">{t}</div><div className="card__b">{b}</div>
              </Reveal>
            ))}
          </div>
          <Reveal className="flow" data-testid="lifecycle">
            {FLOW.map(([b, s]) => <div className="flow__node" key={b}><b>{b}</b><span>{s}</span></div>)}
          </Reveal>
        </div>
      </section>

      {/* PRICING & APPROVAL CALCULATOR -- moved after How it works: it's the
          hands-on follow-through once visitors already know the workflow,
          not another pricing beat stacked right on the tour's own quote
          stage. */}
      <section className="section section--paper" id="calculator">
        <div className="wrap">
          <Heading eyebrow="Pricing and approval calculator" title="Pick a discount, pick a role — see exactly what gets approved, and why." lede="Same approval logic the product enforces on live deals, not a mock-up." />
          <Calculator />
          {/* The NRR/headroom/discount-leakage planning calculators used to be
              re-promoted here too, but they're SEO-indexable pages of their own
              (search intent: "NRR calculator", etc.) reached via the Calculators
              nav tab -- repeating them mid-homepage showed the same offer twice
              to people already on the site instead of reaching anyone new. */}
        </div>
      </section>

      {/* ROLES */}
      <section className="section" id="roles">
        <div className="wrap">
          <Heading eyebrow="Who it's for" title="Built for every role on the deal." />
          <div className="roles">
            {ROLES.map((r, i) => (
              <Reveal as="details" className="card" key={r.role} open={i === 0}>
                <summary>
                  <div className="card__badge">{ROLE_ICONS[i]}</div>
                  <div className="card__t">{r.role}</div>
                  <span className="card__chev">+</span>
                </summary>
                <ul className="card__bullets">
                  <li>{r.feature}</li>
                  <li className="benefit">{r.benefit}</li>
                </ul>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section cta-band" id="pilot">
        <div className="wrap cta-band__grid">
          <div>
            <div className="eyebrow" style={{ color: "#d9b45b" }}>Start a pilot</div>
            <h2 className="h2">Proof, not a pitch.</h2>
            <p className="lede">See it work on your own accounts — not a demo.</p>
            <ul style={{ color: "#cfc8b8", paddingLeft: 18, marginTop: 18, fontSize: 15 }}>
              <li>Real-time numbers, pulled from your own accounts</li>
              <li>Whitespace surfaced the moment you import — not a spreadsheet exercise</li>
              <li>One order form — priced, routed for approval if it needs it, and signed, all in the same sitting</li>
            </ul>
            {SITE.email && <p style={{ color: "#a9a293", fontSize: 13, marginTop: 20 }}>Prefer email? <a href={`mailto:${SITE.email}`} style={{ color: "#d9b45b" }}>{SITE.email}</a></p>}
          </div>
          <LeadForm intent="walkthrough" source="home" showTabs={false} />
        </div>
      </section>

      {/* PRICING */}
      <section className="section section--paper" id="pricing">
        <div className="wrap">
          <Heading eyebrow="Pricing" title="One plan. Everything included." lede="Built for teams already closing expansion revenue." />
          <PlanCards />
        </div>
      </section>

      {/* FAQ */}
      <section className="section" id="faq">
        <div className="wrap" style={{ maxWidth: 860 }}>
          <Heading eyebrow="Questions" title="Straight answers." />
          <FaqList />
        </div>
      </section>

      <div className="sticky-cta" aria-hidden="false">
        <Link to="/pilot" className="btn btn--primary" onClick={() => track("cta_click", { where: "sticky" })}>{CTA.primary}</Link>
      </div>
    </>
  );
}
